// SPDX-FileCopyrightText: Copyright 2025 OP Financial Group (https://op.fi). All Rights Reserved.
//
// SPDX-License-Identifier: LicenseRef-OpAllRightsReserved

import React, { useContext, useState } from 'react';
import PageHeadingTexts from './PageHeadingTexts';
import CompanyForm from './CompanyForm';
import NavigationButtons from '../../components/NavigationButtons';
import AdminInfoNoticeModal from '../../components/AdminInfoNoticeModal';
import AdminInformation from './AdminInformation/AdminInformation';
import { getFirstPageErrors, validateFirstPage, getServiceHasNoAdminErrors } from '../../validation/Validation';
import message from '../../messages';
import { Alert } from '@op/opds.opux';
import MainHeader from '../../components/MainHeader';
import InputErrorTile from '../../components/InputErrorTile';
import { id } from '../../Constants';
import { ContextWrapper, ContractData } from '../../context/ContextWrapper';
import ServiceHasNoAdminErrorTile from '../../components/ServiceHasNoAdminErrorTile';
import './firstPage.scss';

interface FirstPageProps {
    continuePath: string;
    showAlert: boolean;
}

interface ErrorType {
    id: string;
    message: string;
    index?: number;
    isAdminError?: boolean;
}

interface Admin {
    agreementTypes: Record<string, any>;
    canEditRoles?: boolean;
}

const FirstPage: React.FC<FirstPageProps> = ({ continuePath, showAlert }) => {
    const [errors, setErrors] = useState<ErrorType[]>([]);
    const [noAdminErrors, setNoAdminErrors] = useState<string[]>([]);
    const { contractData, isAdminFlow } = useContext(ContextWrapper);
    const [showSelectedAdminNotification, setShowSelectedAdminNotification] = useState<boolean>(false);
    const [serviceTypeWithNoEditRight, setServiceTypeWithNoEditRight] = useState<string[]>([]);

    const continueProcess = (data: ContractData, updateInformation: any): boolean => {
        let isValidated = validateFirstPage(data, updateInformation, isAdminFlow, contractData.showAgreementTypes);
        const errors = getFirstPageErrors(data, isAdminFlow, contractData.showAgreementTypes);
        const noAdminErrors = getServiceHasNoAdminErrors(data);
        const selectedServiceTypes = contractData.companyData.companyTypes;
        const servicesOneAdminNoEditArray: string[] = [];

        setErrors(errors);
        setNoAdminErrors(noAdminErrors);

        if (errors.length > 0 || noAdminErrors.length > 0) {
            const alert = document.getElementById(id.ALERT_NOTIFICATION);
            alert?.scrollIntoView({ behavior: 'smooth' });
            (document.activeElement as HTMLElement)?.blur();
        } else if (!isAdminFlow) {
            selectedServiceTypes.forEach((type: string) => {
                const adminWithRights = contractData.administrators.filter((admin: Admin) =>
                    // TODO: Refactor this code to not nest functions more than 4 levels deep.
                    Object.keys(admin.agreementTypes).find((selected: string) => selected === type)
                );
                adminWithRights.forEach((admin: Admin) => {
                    if (admin.canEditRoles === false && adminWithRights.length === 1) {
                        servicesOneAdminNoEditArray.push(type);
                        setServiceTypeWithNoEditRight(servicesOneAdminNoEditArray);
                        setShowSelectedAdminNotification(true);
                        isValidated = false;
                    }
                });
                window.scrollTo(0, 0);
                (document.activeElement as HTMLElement)?.blur();
            });
        }
        return isValidated;
    };

    return (
        <div className="opux-spacing-bottom-margin-double" data-testid="mainContent">
            <MainHeader />
            <PageHeadingTexts />
            {showAlert && (
                <div className="alert-error-wrapper opux-spacing-bottom-margin-double" data-testid="alertContent">
                    <Alert type="critical" data-testid="adminFlowWelcomeText1">
                        {message('noDataAfterTupasError')}
                    </Alert>
                </div>
            )}
            <div className="alert-notification" id={id.ALERT_NOTIFICATION} aria-hidden="true" />
            <InputErrorTile errors={errors} />
            <ServiceHasNoAdminErrorTile errors={noAdminErrors} />
            <CompanyForm />
            {isAdminFlow || (!isAdminFlow && !contractData.showAgreementTypes) ? (
                <AdminInformation />
            ) : (
                contractData.companyData.companyTypes.length > 0 && <AdminInformation />
            )}
            <AdminInfoNoticeModal
                closeModal={() => setShowSelectedAdminNotification(false)}
                isOpen={showSelectedAdminNotification}
                type={serviceTypeWithNoEditRight}
            />
            <NavigationButtons continuePath={continuePath} cancelEnabled continueProcess={continueProcess} />
        </div>
    );
};

export default FirstPage;
